import { type ThemeColor } from '@precis-digital/kurama/theme'
import type {
  ChartChoiceOption,
  ChartDataRow,
  ChartChoiceOptions,
  Dimension,
  BaseElementDescription,
  DimensionValuesSortType
} from '../types'
import { NULL_DIMENSION_VALUE_DISPLAY } from '../constants'
import { assertNever, getLabelOrValueAsLabel, isNullDimensionValue } from './generics'

const sortChoiceOptions = <DataKey extends string>({
  options,
  sort
}: {
  options: ChartChoiceOptions<DataKey>
  sort: DimensionValuesSortType
}): ChartChoiceOptions<DataKey> => {
  switch (sort) {
    // Sorting options by their metrics values is done down the line,
    // so here we just sort by label so that then two options with same value
    // are sorted alphabetically.
    case 'metricsAsc':
    case 'metricsDesc':
    case 'asc':
      return options.sort(({ value: valueA, label: labelA }, { value: valueB, label: labelB }) =>
        (labelA ?? valueA).localeCompare(labelB ?? valueB)
      )
    case 'desc':
      return options.sort(({ value: valueA, label: labelA }, { value: valueB, label: labelB }) =>
        (labelB ?? valueB).localeCompare(labelA ?? valueA)
      )
    default:
      assertNever(sort)
      return options
  }
}
const createChartChoiceValueOtherOptions = ({
  otherOptions,
  otherOption,
  othersDescription,
  availableColors,
  choiceOptions
}: {
  otherOptions: string[]
  otherOption: string | undefined
  othersDescription: ChartChoiceOption<string> | undefined
  availableColors: Set<ThemeColor>
  choiceOptions: Array<ChartChoiceOption<string>>
}): void => {
  if (otherOption != null) {
    choiceOptions.push({
      label: otherOption,
      ...othersDescription,
      value: otherOption,
      values: new Set(otherOptions)
    })
  } else if (othersDescription == null) {
    const colorsIterator = availableColors.values()
    for (const option of otherOptions) {
      const nextColor = colorsIterator.next()
      if (nextColor.done === true) {
        choiceOptions.push({ value: option, label: option })
      } else {
        choiceOptions.push({ value: option, label: option, color: nextColor.value })
        availableColors.delete(nextColor.value)
      }
    }
  } else {
    choiceOptions.push({ label: othersDescription.value, ...othersDescription, values: new Set(otherOptions) })
  }
}

/**
 * Get the chart choice dimension values options {@link ChartChoiceOption} for a dimension,
 * using the descriptions map if defined.
 * @param dimension The dimension choice {@link Dimension<'choice', DataKey, DataRow>}.
 * @param otherOption The other option (if any).
 * @param options The options (dimension values) to transform.
 * @returns The array of chart choice dimension values options {@link ChartChoiceOption}.
 */
export const createChartChoiceValueOptions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dimension,
  otherOption,
  options,
  sort,
  chartAvailableColors
}: {
  dimension: Dimension<'choice', DataKey, DataRow>
  otherOption: string | undefined
  options: string[]
  sort: DimensionValuesSortType | undefined
  chartAvailableColors: readonly ThemeColor[]
}): Array<ChartChoiceOption<string>> => {
  const { descriptions: valuesDescriptionsMap, othersDescription } = dimension

  let choiceOptions: Array<ChartChoiceOption<string>> | undefined
  const otherOptions: string[] = []
  const availableColors = new Set(chartAvailableColors)

  if (valuesDescriptionsMap == null) {
    choiceOptions = options.map((option, index) => ({
      value: option,
      label: getLabelOrValueAsLabel(undefined, option),
      color: chartAvailableColors[index]
    }))
  } else {
    choiceOptions = createChoiceOptionsAndOtherOptions(options, otherOptions, valuesDescriptionsMap, availableColors)
  }

  if (sort != null) {
    sortChoiceOptions({ options: choiceOptions, sort })
  }

  if (otherOptions.length > 0) {
    const choiceOtherOptions: Array<ChartChoiceOption<string>> = []
    createChartChoiceValueOtherOptions({
      otherOptions,
      otherOption,
      othersDescription,
      availableColors,
      choiceOptions: choiceOtherOptions
    })

    if (sort != null && choiceOtherOptions.length > 1) {
      sortChoiceOptions({ options: choiceOtherOptions, sort })
    }

    choiceOptions.push(...choiceOtherOptions)
  }

  return choiceOptions
}

/**
 * Prefer definition colours; fill gaps from {@link chartSeriesColors} so series stay distinct.
 * Used by performance / score-card groups when metrics omit a colour.
 */
export const assignSeriesColorsFromDefinitions = <T extends object>({
  items,
  getDefinedColor,
  chartSeriesColors
}: {
  items: T[]
  getDefinedColor: (item: T) => ThemeColor | undefined
  chartSeriesColors: readonly ThemeColor[]
}): T[] => {
  const availableColors = new Set(chartSeriesColors)

  for (const item of items) {
    const definedColor = getDefinedColor(item)
    if (definedColor != null) {
      availableColors.delete(definedColor)
    }
  }

  const colorsIterator = availableColors.values()

  return items.map((item) => {
    const definedColor = getDefinedColor(item)
    if (definedColor != null) {
      return { ...item, color: definedColor }
    }

    const nextColor = colorsIterator.next()
    if (nextColor.done === true) {
      return item
    }

    return { ...item, color: nextColor.value }
  })
}

const createChoiceOptionsAndOtherOptions = (
  options: string[],
  otherOptions: string[],
  valuesDescriptionsMap: Record<string, BaseElementDescription>,
  availableColors: Set<ThemeColor>
): Array<ChartChoiceOption<string>> => {
  if (options.length === 0) {
    options = Object.keys(valuesDescriptionsMap)
  }
  const choiceOptions = options
    .filter((option) => {
      // Null is a first-class category (drawn/selected as "-"), not an unlabeled leftover.
      if (option in valuesDescriptionsMap || isNullDimensionValue(option)) {
        return true
      }
      otherOptions.push(option)
      return false
    })
    .map<ChartChoiceOption<string>>((option) => {
      const description = valuesDescriptionsMap[option]
      if (description == null) {
        return { value: option, label: NULL_DIMENSION_VALUE_DISPLAY }
      }

      const { color, ...rest } = description
      if (color != null) {
        availableColors.delete(color)
      }

      if (isNullDimensionValue(option)) {
        return color != null
          ? { value: option, color, ...rest, label: NULL_DIMENSION_VALUE_DISPLAY }
          : { value: option, ...rest, label: NULL_DIMENSION_VALUE_DISPLAY }
      }

      return color != null ? { value: option, color, ...rest } : { value: option, ...rest }
    })

  const colorsIterator = availableColors.values()

  for (const option of choiceOptions) {
    if (option.color == null) {
      const nextColor = colorsIterator.next()
      if (nextColor.done === true) break
      option.color = nextColor.value
      availableColors.delete(nextColor.value)
    }
  }

  return choiceOptions
}
